/// <reference path="../../../node_modules/@1js/office-online-strings/en-US/transcription-strings.d.ts" />

import Dispatcher from '../dispatcher';
import { AppTranscriptCardChangeParameter, ITranscriptData } from '../components';
import { ITranscriptionSegment, ITranscription, ITranscriptionManager } from '../../backend/interfaces/ITranscriptionManager';
import { SetFailureStateAndRouteToFailure, RevertAllStates } from './FailureActions';
import { TranscriptionRoutePathNames } from '../routes';
import { ITranscriptionSourceFile } from '../../backend/interfaces/ITranscriptionSourceFileProvider';
import { DocumentStorageError, DocumentStorageErrors } from '../../backend/office-js/DocumentStorageErrors';
import { LogEventNames } from '../../backend/interfaces/ITelemetryLogger';
import { LogError, ShouldLogErrorAsWarning } from '../../utils/ErrorTelemetry';
import { ErrorStringFromException } from '../../utils/utils';
import { InitializeAudioBackup, AudioBackupAndRecoveryStatus } from '../common/actions/RecoveringActions';
import { NavigateToLandingPageAfterAudioBackupCheck } from './LandingActions';
import { BootError, BootErrors } from '../BootErrors';
import { AudioBackupError, AudioBackupErrors } from '../../backend/audioBackup/AudioBackupErrors';
import { TranscriptionTelemetryErrorCodes } from '../../utils/TranscriptionTelemetryErrorCodes';
import { ITranscriptionErrorEvent } from '../../backend/interfaces/ITranscriptionErrorEvent';
import { AddToDocumentMenuOption, TranscribingActionTypes} from '../common/enums/transcribingEnums';
import { FailureContinuation } from '../common/enums/failureEnums';
import {GetSpeakerName, getAddToDocumentOptionsFromMenuOption} from '../common/actions/TranscribingActions';
import { TranscriptionManagerFactory } from '../../backend/factory/TranscriptionManagerFactory';
import { CreateAddToDocumentProvider } from '../../backend/factory/AddToDocumentProviderFactory';
import { IAddTranscriptionToCanvas } from '../../backend/interfaces/IAddTranscriptionToCanvas';

function GetFailureReasonForTranscribingError(error: any): ITranscriptionErrorEvent {
  if (error instanceof BootError && error.name == BootErrors.IndexeddbFailedToOpen
    || error instanceof AudioBackupError && (error.name == AudioBackupErrors.UnsupportedBrowser || error.name == AudioBackupErrors.IndexedDBOpenError)) {
    return {errorEventMessage : TranscriptionStrings.CookieSettingsFailureReason, errorEventName: TranscriptionTelemetryErrorCodes.CookieSettingsFailureReason};
  }

  return {errorEventMessage : TranscriptionStrings.UnknownInitFailureReason,errorEventName: TranscriptionTelemetryErrorCodes.UnknownInitFailureReason};
}
export function InitializeTranscriptionData(history: any) {
  try {
    const transcriptionManager: ITranscriptionManager = TranscriptionManagerFactory.GetInstance();
    SetTranscriptionManager(transcriptionManager);

    const transcription: ITranscription = transcriptionManager.GetTranscription();
    const segments: ITranscriptionSegment[] = transcription.transcriptSegments;
    const transcriptionData: ITranscriptData[] = segments.map(
      (transcriptionSegment: ITranscriptionSegment, index: number) => {
        // Due to the lack of availability of the speaker details from the OneDrive transcription API we keep the speaker field static
        return {
          id: index,
          text: transcriptionSegment.text,
          timestamp: transcriptionSegment.start,
          speaker: GetSpeakerName(transcriptionSegment.speakerId, transcription.speakerNames)
        };
      }
    );

    Dispatcher.dispatch({
      type: TranscribingActionTypes.SetTranscriptionDataAction,
      payload: transcriptionData
    });
  } catch (error) {
    let continuation:FailureContinuation = FailureContinuation.None;
    if (error instanceof DocumentStorageError && error.name == DocumentStorageErrors.CorruptTranscriptSource) {
      continuation = FailureContinuation.StartOver;
    }

    const transcriptionErrorEvent = GetFailureReasonForTranscribingError(error);
    handleFailure(
      LogEventNames.DocumentStorage,
      transcriptionErrorEvent,
      continuation /*failureContinuation*/,
      'InitializeTranscriptionData threw error: ' + ErrorStringFromException(error),
      history);
  }
}

export async function DeleteStoredTranscriptionAndRouteToLanding(history: any) {
  await TranscriptionManagerFactory.GetInstance().DeleteTranscription();
  RevertAllStates();

  // We want to create a transcription connection and audio backup here to avoid paying the initialization price later, so it improves the "time to input" metric.
  const backupAndState: AudioBackupAndRecoveryStatus | null = await InitializeAudioBackup();
  if (backupAndState != null && backupAndState.needsRecovery) {
    // in case of recovery, the transcription connection is created later from inside the Recovering page
    history.replace(TranscriptionRoutePathNames.Recovering, {audioBackup: backupAndState.audioBackup});
  } else {
    NavigateToLandingPageAfterAudioBackupCheck(backupAndState, history);
  }
}

export async function StartOverClicked(history: any) {
  try {
    await DeleteStoredTranscriptionAndRouteToLanding(history);
  } catch (error: any) {
    const transcriptionErrorEvent = GetFailureReasonForTranscribingError(error);
    handleFailure(
      ShouldLogErrorAsWarning(error) ? LogEventNames.Warning: LogEventNames.DocumentStorage,
      transcriptionErrorEvent,
      FailureContinuation.None /*failureContinuation*/,
      'Starting over threw exception: ' + ErrorStringFromException(error),
      history);
  }
}

export async function TranscriptionDataModified(transcriptionManager: ITranscriptionManager, data: AppTranscriptCardChangeParameter, history: any) {
  try {
    let updatedSpeakerName: string | null = null;
    const transcription = transcriptionManager.GetTranscription();

    // Don't update speaker name if it's being set to the same name we are displaying.
    if (data.speaker != GetSpeakerName(transcription.transcriptSegments[data.id].speakerId, transcription.speakerNames)) {
      updatedSpeakerName = data.speaker;
    }

    await transcriptionManager.UpdateTranscriptionSegment(data.id, data.text, updatedSpeakerName, data.changeAllSpeakers);
    InitializeTranscriptionData(history);
  } catch (error) {
    const transcriptionErrorEvent = GetFailureReasonForTranscribingError(error);
    handleFailure(LogEventNames.DocumentStorage, transcriptionErrorEvent, FailureContinuation.None /*failureContinuation*/, 'TranscriptionData update threw exception: ' + ErrorStringFromException(error), history);
  }
}

export async function AddTranscriptionToDocument(transcriptionManager: ITranscriptionManager, history: any, sourceFile: ITranscriptionSourceFile | null, addOption: AddToDocumentMenuOption, callback?: Function) {
  try {
    const addToDocumentProvider: IAddTranscriptionToCanvas = CreateAddToDocumentProvider();
    await addToDocumentProvider.AddTranscriptionToDocument(transcriptionManager.GetTranscription(), sourceFile, getAddToDocumentOptionsFromMenuOption(addOption), callback);
  } catch(error) {
    const transcriptionErrorEvent = GetFailureReasonForTranscribingError(error);
    handleFailure(LogEventNames.DocumentStorage, transcriptionErrorEvent, FailureContinuation.None /*failureContinuation*/, 'AddTranscriptionToDocument threw exception: ' + ErrorStringFromException(error), history);
  }
}

export function AddTranscriptionSegmentToDocument(transcriptionManager: ITranscriptionManager, id: number, history: any) {
  try {
    const addToDocumentProvider: IAddTranscriptionToCanvas = CreateAddToDocumentProvider();
    addToDocumentProvider.AddTranscriptionSegmentToDocument(transcriptionManager, id);
  } catch(error) {
    const transcriptionErrorEvent = GetFailureReasonForTranscribingError(error);
    handleFailure(LogEventNames.DocumentStorage, transcriptionErrorEvent, FailureContinuation.None /*failureContinuation*/, 'AddTranscriptionSegmentToDocument threw exception: ' + ErrorStringFromException(error), history);
  }
}

export function SetTranscriptionManager(transcriptionManager: ITranscriptionManager) {
  Dispatcher.dispatch({
    type: TranscribingActionTypes.SetTranscriptionManager,
    payload: transcriptionManager
  });
}

function handleFailure(area: LogEventNames, transcriptionErrorEvent : ITranscriptionErrorEvent, continuation: FailureContinuation, failureReasonDebug: string, history: any) {
  LogError(area, failureReasonDebug);
  SetFailureStateAndRouteToFailure(area, transcriptionErrorEvent, continuation, history);
}